/**
 * Map live `V2GridAsset[]` (from `useGridPage().assets`) into the
 * lobby's discriminated `LobbyAdapter[]` so the existing per-adapter
 * panel registry (Flaunch / UniV3 / UniV4 / Aerodrome / Custom) can
 * render the same cards regardless of whether the board's data is
 * mocked or live.
 *
 * The lobby panels were intentionally typed against pre-formatted
 * display strings (e.g. `tvlUsd: "$612k"`) so this adapter does the
 * scaling + formatting once at the boundary instead of forcing every
 * panel component to know about indexer decimals. Numeric inputs from
 * `V2GridAsset` are USDC-6dp / wad-18dp / native-token raw — see field
 * comments in `lib/types/v2.ts`.
 */
import type { GridAdapterNote, V2GridAsset } from '@/lib/types/v2';
import { formatMarketUsd } from '@/lib/utils';
import { WETH_ADDRESS } from '@/lib/constants/addresses';
import type {
  LobbyAdapter,
  FlaunchAdapter,
  UniswapV3Adapter,
  UniswapV4Adapter,
  AerodromeAdapter,
  CustomAdapter,
  AdapterLink,
  LobbyAdapterStatus,
} from './mock-adapters';

const CHAIN_PREFIX =
  process.env.NEXT_PUBLIC_CHAIN_ID === '8453' ? 'base' : 'base-sepolia';

/**
 * Pick the user-facing coin address from a Flaunch asset's pair.
 *
 * Flaunch coins on Base are always paired against WETH
 * (`0x4200…0006`). Uniswap-style pools sort the two sides by raw
 * address, and WETH's `0x42…` prefix sorts ahead of most randomly-
 * generated coin addresses, so the indexer typically lands WETH on
 * `token0Address` and the actual coin on `token1Address` — but this
 * is purely an ordering accident, not a contract.
 *
 * The previous implementation grabbed `token0Address` unconditionally
 * and ended up pointing the outbound Flaunch chip at
 * `flaunch.gg/base/coins/0x42…0006` (the WETH page, which Flaunch
 * either 404s or displays as a generic ETH stub). Fix: explicitly
 * exclude WETH and pick whichever side is left. Falls back to
 * `adapterAddress` so the chip degrades to a still-meaningful link
 * if the indexer ever returns an exotic non-WETH Flaunch pair.
 *
 * Returns `null` when no usable address is available so the caller
 * can decide explicitly (currently: skip emitting the chip) — better
 * than the previous "empty string sentinel" since the type system
 * carries the absence and `coinAddress: pickFlaunch(...) ?? ''`
 * keeps the SDK shape (`coinAddress: string`) stable.
 */
function pickFlaunchCoinAddress(asset: V2GridAsset): string | null {
  const weth = WETH_ADDRESS.toLowerCase();
  const t0 = asset.token0Address?.toLowerCase();
  const t1 = asset.token1Address?.toLowerCase();
  if (t0 && t0 !== weth) return asset.token0Address!;
  if (t1 && t1 !== weth) return asset.token1Address!;
  return asset.adapterAddress ?? null;
}

/** Convert a USDC 6-decimal `BigIntString` into a plain JS number of dollars. */
function usdc6ToDollars(value: string | undefined | null): number {
  if (!value) return 0;
  try {
    return Number(BigInt(value)) / 1e6;
  } catch {
    return 0;
  }
}

/** Convert a wad (18-decimal) `BigIntString` into a plain JS number. */
function wadToFloat(value: string | undefined | null): number {
  if (!value) return 0;
  try {
    return Number(BigInt(value)) / 1e18;
  } catch {
    return 0;
  }
}

/**
 * APR derived from a 7-day yield window. We extrapolate (not compound) so
 * the displayed % matches the rest of the app's rolling-window APR
 * convention. Returns a formatted string ("47.2%") or `null` when we
 * lack the inputs to compute it (TVL = 0 ⇒ undefined APR).
 */
function deriveApr(yield7dUsd: number, tvlUsd: number): string {
  if (tvlUsd <= 0 || yield7dUsd <= 0) return '0.0%';
  const apr = (yield7dUsd / tvlUsd) * (365 / 7) * 100;
  return `${apr.toFixed(1)}%`;
}

/**
 * Some adapters report TVL in native (ETH) units when the indexer can't
 * resolve a USD price (`priceable === false`). For exotic / illiquid
 * pairs we return a "—" placeholder rather than a misleading "$0".
 */
function formatTvl(asset: V2GridAsset): string {
  const usd = usdc6ToDollars(asset.tvlUsd);
  if (usd > 0) return formatMarketUsd(usd);
  if (asset.priceable === false) return '—';
  return '$0';
}

/**
 * Yield strings: "Accrued" is total yield received in whatever the asset
 * pays out in. We display it scaled to whole token units (decimals fall
 * back to 18 wad which is the wad assumption used elsewhere when the
 * indexer hasn't resolved a side).
 */
function formatAccrued(asset: V2GridAsset): { accrued: string; accruedUsd: string } {
  const totalUsd = usdc6ToDollars(asset.totalYieldReceived);
  if (totalUsd > 0) {
    return { accrued: formatMarketUsd(totalUsd), accruedUsd: '' };
  }
  // Fallback: treat the raw amount as wad and show 4dp — better than 0.
  const native = wadToFloat(asset.totalYieldReceived);
  return {
    accrued: native > 0 ? native.toFixed(4) : '0',
    accruedUsd: '',
  };
}

function statusFor(asset: V2GridAsset): LobbyAdapterStatus {
  if (!asset.active) return 'paused';
  if (asset.withdrawnAt) return 'cooldown';
  return 'live';
}

function basescanLink(address: string): AdapterLink {
  const host =
    process.env.NEXT_PUBLIC_CHAIN_ID === '8453'
      ? 'https://basescan.org'
      : 'https://sepolia.basescan.org';
  return {
    href: `${host}/address/${address}`,
    label: 'Adapter',
    icon: 'globe',
  };
}

function pairFromAsset(asset: V2GridAsset): string {
  if (asset.pairLabel) return asset.pairLabel;
  const a = asset.token0Symbol ?? '?';
  const b = asset.token1Symbol ?? '?';
  return `${a} / ${b}`;
}

/** Resolve which discriminated adapter kind to use given the indexer's
 * `assetType` (preferred) and `platform` (fallback). The indexer can
 * report `lp` for either v3 or v4, so we look at `platform` to break
 * the tie when both are present. */
function resolveKind(asset: V2GridAsset): LobbyAdapter['kind'] {
  if (asset.platform === 'flaunch' || asset.assetType === 'flaunch') return 'flaunch';
  if (asset.platform === 'aerodrome') return 'aerodrome';
  if (asset.platform === 'uniswap-v4' || asset.assetType === 'lp-v4') return 'uniswap-v4';
  if (asset.platform === 'uniswap-v3' || asset.assetType === 'lp') return 'uniswap-v3';
  return 'custom';
}

function flaunch(asset: V2GridAsset): FlaunchAdapter {
  const tvlUsdNum = usdc6ToDollars(asset.tvlUsd);
  const yield7dUsd = usdc6ToDollars(asset.yield7d);
  const { accrued, accruedUsd } = formatAccrued(asset);
  const symbol = asset.symbol ?? asset.token0Symbol ?? 'COIN';
  // The SDK's `FlaunchAdapter.coinAddress` field is `string`, so we
  // collapse the `string | null` from the picker to `''` when no
  // address is available. The links array below already gates on
  // truthiness so a `''` coinAddress means "no chip emitted",
  // matching the test expectations in
  // `src/features/lobby/data/__tests__/grid-to-adapters.test.ts`.
  const coinAddress = pickFlaunchCoinAddress(asset) ?? '';

  return {
    id: asset.id,
    kind: 'flaunch',
    label: asset.name ?? `Flaunch · ${symbol}`,
    // Subtitle was the noisy "$SYMBOL buybacks → seat rewards" tagline.
    // Replaced with a flat "Flaunch token" descriptor — the buyback /
    // seat-rewards mechanic is documented in the adapter guide and the
    // panel's header + outbound Flaunch link already make the brand
    // clear without restating the mechanic per row.
    subtitle: 'Flaunch token',
    status: statusFor(asset),
    tvlUsd: formatTvl(asset),
    apr: deriveApr(yield7dUsd, tvlUsdNum),
    accrued,
    accruedUsd,
    coinSymbol: symbol,
    coinAddress,
    // Single outbound link by design — Flaunch's own coin page covers
    // price / chart / trade, so the previous Dexscreener + adapter
    // (basescan) chips were redundant chrome. The chip now renders
    // text + icon + ext-link arrow, so the bare "Flaunch" label is
    // unambiguous on its own.
    links: coinAddress
      ? [
          {
            href: `https://flaunch.gg/${CHAIN_PREFIX}/coins/${coinAddress}`,
            label: 'Flaunch',
            icon: 'flaunch' as const,
          },
        ]
      : [],
  };
}

function uniV3(asset: V2GridAsset): UniswapV3Adapter {
  const tvlUsdNum = usdc6ToDollars(asset.tvlUsd);
  const yield7dUsd = usdc6ToDollars(asset.yield7d);
  const { accrued, accruedUsd } = formatAccrued(asset);
  const pair = pairFromAsset(asset);

  return {
    id: asset.id,
    kind: 'uniswap-v3',
    label: asset.name ?? `Uniswap v3 · ${pair}`,
    subtitle: `${pair} LP fees → seat deposits`,
    status: statusFor(asset),
    tvlUsd: formatTvl(asset),
    apr: deriveApr(yield7dUsd, tvlUsdNum),
    accrued,
    accruedUsd,
    pair,
    feeBps: asset.feeTier ?? 0,
    isFullRange: asset.isFullRange ?? false,
    tickLower: asset.tickLower,
    tickUpper: asset.tickUpper,
    links: [
      ...(asset.adapterAddress ? [basescanLink(asset.adapterAddress)] : []),
    ],
  };
}

function uniV4(asset: V2GridAsset): UniswapV4Adapter {
  const tvlUsdNum = usdc6ToDollars(asset.tvlUsd);
  const yield7dUsd = usdc6ToDollars(asset.yield7d);
  const { accrued, accruedUsd } = formatAccrued(asset);
  const pair = pairFromAsset(asset);

  return {
    id: asset.id,
    kind: 'uniswap-v4',
    label: asset.name ?? `Uniswap v4 · ${pair}`,
    subtitle: `${pair} v4 hook fees → seats`,
    status: statusFor(asset),
    tvlUsd: formatTvl(asset),
    apr: deriveApr(yield7dUsd, tvlUsdNum),
    accrued,
    accruedUsd,
    pair,
    feeBps: asset.feeTier ?? 0,
    // Hook descriptor isn't on V2GridAsset yet — surface the adapter
    // address truncation so users can verify on Basescan in the
    // meantime, and we can swap in a hook label when the indexer
    // resolves it.
    hookSummary: asset.adapterAddress
      ? `Hook · ${asset.adapterAddress.slice(0, 6)}…${asset.adapterAddress.slice(-4)}`
      : undefined,
    links: [
      ...(asset.adapterAddress ? [basescanLink(asset.adapterAddress)] : []),
    ],
  };
}

function aerodrome(asset: V2GridAsset): AerodromeAdapter {
  const tvlUsdNum = usdc6ToDollars(asset.tvlUsd);
  const yield7dUsd = usdc6ToDollars(asset.yield7d);
  const { accrued, accruedUsd } = formatAccrued(asset);
  const pair = pairFromAsset(asset);

  return {
    id: asset.id,
    kind: 'aerodrome',
    label: asset.name ?? `Aerodrome · ${pair}`,
    subtitle: `${pair} AERO gauge → seats`,
    status: statusFor(asset),
    tvlUsd: formatTvl(asset),
    apr: deriveApr(yield7dUsd, tvlUsdNum),
    accrued,
    accruedUsd,
    pair,
    // Gauge weight + veBoost aren't surfaced by V2GridAsset; we leave
    // them empty so the panel renders the gauge link without
    // fabricated multipliers.
    gaugeWeightPct: '—',
    veBoostMultiplier: undefined,
    links: [
      ...(asset.adapterAddress ? [basescanLink(asset.adapterAddress)] : []),
    ],
  };
}

function promoFromNote(note: GridAdapterNote | undefined): LobbyAdapter['promo'] | undefined {
  if (!note) return undefined;
  const extra: AdapterLink[] = [];
  if (note.websiteUrl?.trim()) {
    extra.push({ href: note.websiteUrl.trim(), label: 'Site', icon: 'globe' });
  }
  // Per-asset X URLs were retired — the board-level OAuth-verified
  // `twitterHandle` is the only sanctioned X surface in the lobby. Any
  // legacy `twitterUrl` still sitting on `pool_metadata.adapter_notes`
  // is intentionally dropped here so it never renders as a chip.
  if (note.telegramUrl?.trim()) {
    extra.push({ href: note.telegramUrl.trim(), label: 'Telegram', icon: 'globe' });
  }
  if (note.discordUrl?.trim()) {
    extra.push({ href: note.discordUrl.trim(), label: 'Discord', icon: 'globe' });
  }
  const hasCopy =
    !!note.title?.trim() ||
    !!note.description?.trim() ||
    !!note.imageUrl?.trim() ||
    extra.length > 0;
  if (!hasCopy) return undefined;
  return {
    title: note.title?.trim() || undefined,
    description: note.description?.trim() || undefined,
    imageUrl: note.imageUrl?.trim() || undefined,
    extraLinks: extra,
  };
}

function attachPromo(adapter: LobbyAdapter, note: GridAdapterNote | undefined): LobbyAdapter {
  const promo = promoFromNote(note);
  if (!promo) return adapter;
  return { ...adapter, promo };
}

function custom(asset: V2GridAsset): CustomAdapter {
  const tvlUsdNum = usdc6ToDollars(asset.tvlUsd);
  const yield7dUsd = usdc6ToDollars(asset.yield7d);
  const { accrued, accruedUsd } = formatAccrued(asset);
  const name = asset.name ?? asset.symbol ?? 'Custom adapter';
  const address = asset.adapterAddress ?? asset.id;

  return {
    id: asset.id,
    kind: 'custom',
    label: name,
    subtitle: `Custom adapter · ${name}`,
    status: statusFor(asset),
    tvlUsd: formatTvl(asset),
    apr: deriveApr(yield7dUsd, tvlUsdNum),
    accrued,
    accruedUsd,
    contractName: name,
    contractAddress: address,
    headlineMetric: asset.symbol
      ? `${asset.symbol} · custom yield`
      : 'Custom on-chain yield',
    links: [
      ...(asset.adapterAddress ? [basescanLink(asset.adapterAddress)] : []),
    ],
  };
}

/**
 * Map a single `V2GridAsset` into the discriminated `LobbyAdapter`
 * variant matching its platform/assetType.
 */
export function gridAssetToAdapter(
  asset: V2GridAsset,
  adapterNotes?: Record<string, GridAdapterNote>,
): LobbyAdapter {
  const base: LobbyAdapter = (() => {
    switch (resolveKind(asset)) {
      case 'flaunch':
        return flaunch(asset);
      case 'uniswap-v3':
        return uniV3(asset);
      case 'uniswap-v4':
        return uniV4(asset);
      case 'aerodrome':
        return aerodrome(asset);
      case 'custom':
      default:
        return custom(asset);
    }
  })();
  return attachPromo(base, adapterNotes?.[asset.id]);
}

/**
 * Convenience batch-mapper that filters out withdrawn / inactive assets
 * so the panel stack defaults to "what's currently funding seats" rather
 * than every asset that's ever been registered against the grid. Pass
 * `{ includeInactive: true }` to keep withdrawn entries (useful for
 * admin tooling).
 */
export function gridAssetsToAdapters(
  assets: V2GridAsset[],
  opts: { includeInactive?: boolean; adapterNotes?: Record<string, GridAdapterNote> } = {},
): LobbyAdapter[] {
  const filtered = opts.includeInactive
    ? assets
    : assets.filter((a) => a.active && !a.withdrawnAt);
  return filtered.map((a) => gridAssetToAdapter(a, opts.adapterNotes));
}
